import { moduleMappings, ModuleId } from "@xmiles/holopix-types";
import { CANVAS_DRAFT_FAMILY_IDS, EXPLORE_FAMILY_IDS } from "@/constants/a3ModuleIds";

// 生图模块枚举
export const module = moduleMappings.idToKey;

export enum ModuleEnum {
  explore = 1,
  colour = 3,
  refine = 5,
  sketch = 13,
  fission = 14,
  /** 仅生成了3D模型 */
  generateThreeTransform = 11,
  /** 3D模型 */
  threeTransform = 12,
  matting = 19,
  replace = 18,
  zoom = 20,
  expansion = 22,
  palette = 23,
  pixelate = 24,
  aivideo = 25,
  downloadVideo = 26,
  expression = 28,
  render = 29,
  // module 30 为nano banner,31豆包，32是极速编辑
  nanoBanner = 30,
  doubao = 31,
  Klein = 32,

  freePerspective = 33,
  styleRenderTrain = 41,
  /** 全能编辑 V3 企业专线 */
  gptImage2Enterprise = 46,
}

/**
 * 智能扩图 V2 工作流 modelId（提交 params.module；Pinia / 页面路由仍用 expansion=22）
 * 不作为 ModuleEnum 成员，避免污染 ModuleEnum[id] → 路由名 映射。
 */
export const VISUAL_EXPANSION_V2_MODEL_ID = 50;

/**
 * 智能编辑 V2 工作流 modelId（提交 params.module；Pinia / 页面路由仍用 replace=18）
 */
export const INTELLIGENT_EDIT_V2_MODEL_ID = 51;

/**
 * 重绘放大工作流 modelId（提交 params.module；Pinia / 页面路由仍用 zoom=20）
 * 不作为 ModuleEnum 成员，避免污染 ModuleEnum[id] → 路由名 映射。
 */
export const REPAINT_UPSCALE_MODULE_ID = 59;

/** Seedance 2.0 模块 ID（75-78），用于"编辑→aivideo"路由判定（D6 口径） */
export const AI_SEEDANCE_MODULE_IDS = [
  ModuleId.AiSeedanceStartEndVideo,     // 75
  ModuleId.AiSeedanceReferenceVideo,    // 76
  ModuleId.AiSeedanceImage2Video,       // 77
  ModuleId.AiSeedanceText2Video,        // 78
  ModuleId.AiSeedanceMiniStartEndVideo, // 79
  ModuleId.AiSeedanceMiniReferenceVideo, // 80
  ModuleId.AiSeedanceMiniImage2Video, // 81
  ModuleId.AiSeedanceMiniText2Video, // 82
] as const;

/** 判断模块是否为 Seedance 2.0（75-78），不含旧火山 25、不含 Seedance Mini 79-82 */
export function isSeedanceModule(module: number | null | undefined): boolean {
  if (module == null) return false;
  return (AI_SEEDANCE_MODULE_IDS as readonly number[]).includes(Number(module));
}

/** AI 创作视频模块 ID 集合（旧火山视频 25 + Seedance 2.0 75-78 + Seedance Mini 79-82） */
export const AI_VIDEO_MODULE_IDS = [
  ModuleId.Aivideo, // 25
  ModuleId.AiSeedanceStartEndVideo, // 75
  ModuleId.AiSeedanceReferenceVideo, // 76
  ModuleId.AiSeedanceImage2Video, // 77
  ModuleId.AiSeedanceText2Video, // 78
  ModuleId.AiSeedanceMiniStartEndVideo, // 79
  ModuleId.AiSeedanceMiniReferenceVideo, // 80
  ModuleId.AiSeedanceMiniImage2Video, // 81
  ModuleId.AiSeedanceMiniText2Video, // 82
] as const;

/** 判断模块是否为 AI 创作视频模块（旧火山 + Seedance 2.0 + Seedance Mini） */
export function isAiVideoModule(module: number | null | undefined): boolean {
  if (module == null) return false;
  return (AI_VIDEO_MODULE_IDS as readonly number[]).includes(Number(module));
}

/**
 * 将工作流/版本里的 module id 归一化为前端 store 路由模块 id。
 */
export function resolveStoreModuleId(module: number): ModuleEnum {
  const id = Number(module);
  if (id === VISUAL_EXPANSION_V2_MODEL_ID) {
    return ModuleEnum.expansion;
  }
  if (id === INTELLIGENT_EDIT_V2_MODEL_ID) {
    return ModuleEnum.replace;
  }
  if (id === REPAINT_UPSCALE_MODULE_ID) {
    return ModuleEnum.zoom;
  }
  if (id === ModuleId.QwenFreePerspective) {
    return ModuleEnum.freePerspective;
  }
  // A3 一键成稿：详情/再编辑仍走 Explore 面板 store
  if (id === ModuleId.ExploreA3) {
    return ModuleEnum.explore;
  }

  let ainewVideoModuleIds = [
    ModuleId.AiSeedanceReferenceVideo,
    ModuleId.AiSeedanceStartEndVideo,
    ModuleId.AiSeedanceImage2Video,
    ModuleId.AiSeedanceText2Video,
    ModuleId.AiSeedanceMiniStartEndVideo,
    ModuleId.AiSeedanceMiniReferenceVideo,
    ModuleId.AiSeedanceMiniImage2Video,
    ModuleId.AiSeedanceMiniText2Video
  ];
  // AI 创作视频模块 75-82 归一化到 aivideo store key
  if (ainewVideoModuleIds.includes(id as any)) {
    return ModuleEnum.aivideo;
  }
  return id as ModuleEnum;
}

export function isExpansionModuleId(module: number): boolean {
  const id = Number(module);
  return id === ModuleEnum.expansion || id === VISUAL_EXPANSION_V2_MODEL_ID;
}

export function isReplaceModuleId(module: number): boolean {
  const id = Number(module);
  return id === ModuleEnum.replace || id === INTELLIGENT_EDIT_V2_MODEL_ID;
}

export function isZoomModuleId(module: number): boolean {
  const id = Number(module);
  return id === ModuleEnum.zoom || id === REPAINT_UPSCALE_MODULE_ID;
}
export type ModuleFilterValue = number | number[] | null;

/**
 * 支持滑动对比展示的模块（原图 vs 结果图）
 * 万能渲染、重绘放大等有原图参考的模块，结果展示使用 ImageCompareBox 对比模式
 */
const COMPARE_MODE_MODULE_IDS: number[] = [ModuleId.Render, ModuleId.CanvasRender, ModuleId.RepaintUpscale, ModuleId.CanvasRepaintUpscale];

export function isCompareModeModule(module: number): boolean {
  return COMPARE_MODE_MODULE_IDS.includes(Number(module));
}

/**
 * 版本列表筛选：选择同一业务能力时需同时包含新旧工作流记录。
 */
export function resolveVersionListQueryModule(module: ModuleFilterValue): { module?: number | null; moduleList?: number[] } {
  if (module == null) {
    return { module: null };
  }
  if (Array.isArray(module)) {
    return { moduleList: module };
  }
  if (module === ModuleEnum.expansion) {
    return { moduleList: [ModuleEnum.expansion, VISUAL_EXPANSION_V2_MODEL_ID] };
  }
  if (module === ModuleEnum.replace) {
    return { moduleList: [ModuleEnum.replace, INTELLIGENT_EDIT_V2_MODEL_ID] };
  }
  if ([ModuleId.FreePerspective, ModuleId.QwenFreePerspective].includes(module as any)) {
    return { moduleList: [ModuleEnum.freePerspective, ModuleId.QwenFreePerspective] };
  }
  // A3 与同族旧工作流（Explore 1 / 画布一键成稿 47）合并查询，版本列表不漏 A3 记录
  if (EXPLORE_FAMILY_IDS.includes(module as any)) {
    return { moduleList: [...EXPLORE_FAMILY_IDS] as number[] };
  }
  if (CANVAS_DRAFT_FAMILY_IDS.includes(module as any)) {
    return { moduleList: [...CANVAS_DRAFT_FAMILY_IDS] as number[] };
  }
  // AI 创作视频：旧火山(25) + Seedance 2.0(75-78) 合并查询
  if (module === ModuleEnum.aivideo) {
    return { moduleList: [...AI_VIDEO_MODULE_IDS] as number[] };
  }
  return { module };
}

/**
 * 使用websocket的模块,可以切换队列
 */
export const websocketModules = [
  ModuleId.Explore,
  ModuleId.ExploreA3,
  ModuleId.Colour,
  ModuleId.Refine,
  ModuleId.Sketch,
  ModuleId.Fission,
  ModuleId.Expansion,
  ModuleId.Replace,
  ModuleId.ThreeTransform,
  ModuleId.Expression,
  ModuleId.Render,

  //新旧自由视角
  ModuleId.FreePerspective,
  ModuleId.QwenFreePerspective,

  ModuleId.StyleRenderTrain,
  ModuleId.GptImage2Enterprise,
  ModuleId.RepaintUpscale, // 重绘放大 (module=59)
];
